/**
 * Agent Access & Protection - one agent's associated resources, the native
 * policies on its data sources, its MCP policy rules, a simulated coverage
 * assessment and the enforcement-path verification, as one record.
 *
 * TEMPORARY, mock-only prototype contract. No backend exists: every method
 * rejects outside `<MockScope>`, and the feature is gated by
 * `AGENT_FEATURES.accessProtection` (`live: false`). The open backend
 * questions are recorded on that entry.
 *
 * Four concepts are kept apart on purpose and must never be merged into one
 * status: resource ASSOCIATION (origin + verification), policy APPLICABILITY
 * (the assessment), policy DEPLOYMENT (per native policy), and ENFORCEMENT
 * verification (per MCP gateway path).
 */
import type {
  AgentPolicyRecommendation,
  AgentScope,
  AgentTechnology,
  ApplyPoliciesRequest,
  ApplyPoliciesResult,
  AgentPolicyCoverage,
  EstateAgentToolClassification,
  EstateAgentToolRisk,
  PolicyApplicationPreview,
  PolicyReview,
  PolicyReviewQuery,
} from "@/api/temp/AgentEstateService";
import type { AgentPolicyEffect } from "@/api/temp/AgentPoliciesService";
import type { ConditionEntry } from "@/components/ConditionBuilder/types";

/* ------------------------------------------------------------- association */

/** How the agent came to use the resource: provenance, never evidence. */
export type AssociationOrigin = "DISCOVERED" | "MANUAL";

/** Evidence that the agent really reaches the resource - separate from origin. */
export type AssociationVerification = "NOT_VERIFIED" | "VERIFIED" | "FAILED";

/** How an administrator says the agent reaches a data source. An assertion. */
export type AccessPath = "NOT_SPECIFIED" | "THROUGH_MCP" | "DIRECT";

export type ResourceKind = "MCP_SERVICE" | "DATA_SOURCE";

export interface AgentResourceLink {
  resourceKind: ResourceKind;
  resourceId: string;
  origin: AssociationOrigin;
  verification: AssociationVerification;
  /** Data sources only. */
  accessPath?: AccessPath;
  /** Data sources reached THROUGH_MCP: MCP services already on this agent. */
  linkedMcpServiceIds?: string[];
  note?: string;
  addedAt?: string;
  addedBy?: string;
}

/* ---------------------------------------------------------------- registry */

export interface ProtectionTool {
  id: string;
  name: string;
  description?: string;
  classification: EstateAgentToolClassification;
  risk: EstateAgentToolRisk;
  /** Only where the registry records which source the tool reads. */
  dataSourceId?: string;
}

/** An MCP service registered in TrustLogix (not necessarily on this agent). */
export interface RegisteredMcpService {
  id: string;
  name: string;
  technology: AgentTechnology;
  /** e.g. "Snowflake Data Platform". */
  platformLabel: string;
  description: string;
  tools: ProtectionTool[];
  /**
   * The tool count when the registry reports a count, not the tools (live);
   * `null` when it reports neither. Unset: count `tools`.
   */
  toolCount?: number | null;
  /** Data sources the registry records behind the service. */
  dataSourceIds: string[];
  /** The registry does not report the data sources behind it (live). */
  dataSourcesUnknown?: boolean;
  /** The MCP Registry's id for this server, when the registry knows it. */
  registryServerId?: string;
}

/** A data source registered in TrustLogix (not necessarily on this agent). */
export interface RegisteredDataSource {
  id: string;
  name: string;
  /** Display name, e.g. "Snowflake". */
  platform: string;
  /** `AccountTypes` key, for the logo. */
  platformId: string;
  /** e.g. "Production". */
  environment: string;
  description?: string;
  /** TrustLogix can author and deploy native policies on this platform. */
  nativePolicySupported: boolean;
}

/* --------------------------------------------------------- native policies */

export type NativePolicyType = "ROW_ACCESS" | "DATA_MASKING" | "OBJECT_ACCESS";

export type NativePolicyStatus = "DRAFT" | "APPLYING" | "DEPLOYED" | "FAILED";

export type MappingVerification = "NOT_VERIFIED" | "VERIFIED" | "FAILED";

/** The native platform identity a policy is written for. Never the agent. */
export interface NativePrincipal {
  type: "ROLE";
  name: string;
  mappingSource: "ADMINISTRATOR";
  verification: MappingVerification;
}

export interface NativePolicy {
  id: string;
  dataSourceId: string;
  type: NativePolicyType;
  name: string;
  description?: string;
  /** Catalog object ids, e.g. `CRM.CUSTOMERS`. */
  targetObjectIds: string[];
  /** Masking: the masked columns, e.g. `CRM.CUSTOMERS.EMAIL`. */
  targetColumns?: string[];
  /** Object access: granted privileges. */
  privileges?: string[];
  principal?: NativePrincipal;
  combinator: "AND" | "OR";
  conditions: ConditionEntry[];
  status: NativePolicyStatus;
  /** The last failed deployment's reason. */
  lastError?: DeploymentError;
  /** The agent the author was working from - context, never exclusivity. */
  authoredFromAgentId?: string;
  updatedAt: string;
}

/* ------------------------------------------------------------ MCP policies */

export type ProtectionRuleScope = "AGENT" | "SERVER" | "TOOL";

/**
 * Who owns a seeded MCP policy, which decides where it is changed: an AGENT
 * policy in Policy Management, a SHARED server policy in MCP Services. Rules
 * authored for the agent are authored agent policies (AgentPoliciesService).
 */
export type McpPolicyOwnership = "AGENT" | "SHARED";

export type McpPolicyManagedIn = "POLICY_MANAGEMENT" | "MCP_SERVICES";

/** An MCP access policy that owns one or more rules reaching this agent. */
export interface McpPolicy {
  id: string;
  name: string;
  ownership: McpPolicyOwnership;
  managedIn: McpPolicyManagedIn;
  /** How many agents the policy reaches - 1 for an agent-specific policy. */
  appliesToAgentCount: number;
  updatedAt: string;
}

export type ProtectionRuleDeployment = "DEPLOYED" | "PENDING" | "FAILED";

/** One MCP rule that applies to the agent, and the policy that owns it. */
export interface ProtectionRule {
  id: string;
  /** The owning `McpPolicy`. */
  policyId: string;
  name: string;
  description?: string;
  scope: ProtectionRuleScope;
  /** SERVER and TOOL rules. */
  mcpServiceId?: string;
  /** TOOL rules: the explicit tools. */
  toolIds: string[];
  effect: AgentPolicyEffect;
  combinator: "AND" | "OR";
  conditions: ConditionEntry[];
  /** Added by applying a recommendation. */
  recommendationId?: string;
  deployment?: ProtectionRuleDeployment;
  updatedAt: string;
}

/* -------------------------------------------------------------- assessment */

export type AssessmentState = "NOT_EVALUATED" | "CURRENT" | "STALE" | "ERROR";

/**
 * A restriction the agent's definition requires. Seeded for the demo - the
 * mock evaluator compares these with what is deployed; it never infers one.
 */
export interface ProtectionExpectation {
  id: string;
  title: string;
  /** e.g. "Texas customer records only". */
  expected: string;
  /** Where the requirement comes from. */
  reason: string;
  /**
   * The agent attribute the requirement is grounded in, e.g. Authorized
   * territory = Texas (TX). Never inferred: no attribute, no expectation.
   */
  authorization: { attribute: string; value: string; scope: string };
  severity: "HIGH" | "MEDIUM" | "LOW";
  dataSourceId: string;
  objectId: string;
  policyType: NativePolicyType;
  condition: { attribute: string; operator: string; value: string };
  /** The access path the restriction protects. */
  mcpServiceId: string;
  toolId: string;
  /** Prefills Create Row Access Policy from the gap. */
  suggestedPolicyName?: string;
  suggestedPolicyDescription?: string;
}

export interface ProtectionFinding {
  expectationId: string;
  satisfied: boolean;
  /** Each native policy inspected, and why it does or does not satisfy. */
  inspected: Array<{ policyId: string; satisfies: boolean; verdict: string }>;
}

export interface ProtectionAssessment {
  state: AssessmentState;
  assessedAt?: string;
  findings: ProtectionFinding[];
  /**
   * The data sources the assessment covered. A source associated after it
   * ran reads Not evaluated - never a gap, never covered.
   */
  evaluatedDataSourceIds: string[];
}

/* ------------------------------------------------------------- enforcement */

/** UNKNOWN: setup could not be read - never reported as missing. */
export type GatewaySetup = "CONFIGURED" | "NOT_CONFIGURED" | "UNKNOWN";

/** UNKNOWN/PENDING is never a confirmed gap; CONFIRMED_BYPASS is. */
export type EnforcementVerification =
  | "PENDING"
  | "VERIFIED"
  | "CONFIRMED_BYPASS";

export interface GatewayPath {
  mcpServiceId: string;
  setup: GatewaySetup;
  verification: EnforcementVerification;
  checkedAt?: string;
}

/* -------------------------------------------------------------- deployment */

export interface DeploymentError {
  code: "INVALID_ROLE_MAPPING" | "SIMULATED_FAILURE";
  message: string;
  remediation: string;
}

export type DeploymentOperationKind =
  | "MCP_RULE"
  | "NATIVE_POLICY"
  | "EXISTING_UNCHANGED"
  | "GATEWAY_VERIFICATION";

export type DeploymentOperationStatus =
  | "SUCCEEDED"
  | "FAILED"
  | "UNCHANGED"
  | "PENDING"
  | "VERIFIED"
  | "CONFIRMED_BYPASS";

export interface DeploymentOperation {
  id: string;
  kind: DeploymentOperationKind;
  /** The policy, rule or MCP service the operation is about. */
  targetId: string;
  label: string;
  /** Only attempted operations count toward "N of M succeeded". */
  attempted: boolean;
  status: DeploymentOperationStatus;
  attempts: number;
  error?: DeploymentError;
}

export interface DeploymentRun {
  id: string;
  startedAt: string;
  completedAt: string;
  operations: DeploymentOperation[];
}

/* ------------------------------------------------------------ the record */

/** An associated MCP service, resolved against the registry. */
export interface ProtectionMcpService extends RegisteredMcpService {
  link: AgentResourceLink;
}

/** An associated data source, resolved against the registry. */
export interface ProtectionDataSource extends RegisteredDataSource {
  link: AgentResourceLink;
  /**
   * MCP services on this agent that reach the source - recorded registry
   * relationships plus the administrator's access path, each listed once.
   */
  viaMcpServiceIds: string[];
}

export interface ProtectionScenario {
  /** The next association of Support Archive fails once. */
  failNextAssociation: boolean;
}

export interface AgentProtection {
  agentId: string;
  agentName: string;
  /** True for the seeded demo agent; others are projected from the estate. */
  seeded: boolean;
  mcpServices: ProtectionMcpService[];
  dataSources: ProtectionDataSource[];
  nativePolicies: NativePolicy[];
  /** Every MCP policy owning a rule in `mcpRules`. */
  mcpPolicies: McpPolicy[];
  /** Every MCP rule that applies to this agent, whatever policy owns it. */
  mcpRules: ProtectionRule[];
  expectations: ProtectionExpectation[];
  assessment: ProtectionAssessment;
  gatewayPaths: GatewayPath[];
  lastRun?: DeploymentRun;
  scenario: ProtectionScenario;
}

/* ---------------------------------------------------------------- catalog */

export interface CatalogColumn {
  name: string;
  label: string;
  type: "STRING" | "NUMBER" | "DATE";
}

export interface CatalogObject {
  /** `SCHEMA.TABLE`. */
  id: string;
  schema: string;
  name: string;
  kind: "TABLE" | "VIEW";
  columns: CatalogColumn[];
}

export interface NativeRole {
  name: string;
  /**
   * Fixture-flagged: a stale or invalid mapping. Deploying with it fails -
   * the deterministic way the demo shows a failed native deployment.
   */
  stale?: boolean;
  description: string;
}

export interface DataSourceCatalog {
  dataSourceId: string;
  objects: CatalogObject[];
  roles: NativeRole[];
}

/* ------------------------------------------------------------- requests */

export interface AgentProtectionQuery {
  /** Optional: the agent's own environment is used when none is given. */
  environmentId?: string;
  agentId: string;
}

export interface ProtectionCandidate<T> {
  resource: T;
  /** Already on this agent: shown, never selectable. */
  alreadyAdded: boolean;
}

export interface ProtectionCandidates {
  mcpServices: ProtectionCandidate<RegisteredMcpService>[];
  dataSources: ProtectionCandidate<RegisteredDataSource>[];
}

export interface AssociateResourcesRequest extends AgentProtectionQuery {
  resourceKind: ResourceKind;
  resourceIds: string[];
  actor: string;
}

export interface AssociationResult {
  resourceId: string;
  outcome: "ADDED" | "ALREADY_ADDED" | "FAILED";
  message?: string;
}

export interface UpdateAssociationRequest extends AgentProtectionQuery {
  dataSourceId: string;
  accessPath: AccessPath;
  linkedMcpServiceIds: string[];
  note?: string;
}

export interface RemoveAssociationRequest extends AgentProtectionQuery {
  resourceKind: ResourceKind;
  resourceId: string;
}

export interface NativePolicyInput {
  id?: string;
  dataSourceId: string;
  type: NativePolicyType;
  name: string;
  description?: string;
  targetObjectIds: string[];
  targetColumns?: string[];
  privileges?: string[];
  principalName?: string;
  principalVerification?: MappingVerification;
  combinator: "AND" | "OR";
  conditions: ConditionEntry[];
}

export interface SaveNativePolicyRequest extends AgentProtectionQuery {
  policy: NativePolicyInput;
}

export interface DeployChangesRequest extends AgentProtectionQuery {
  /** The native policy to deploy. */
  nativePolicyId: string;
}

export interface RetryFailedRequest extends AgentProtectionQuery {
  runId: string;
}

export interface VerifyMappingRequest extends AgentProtectionQuery {
  dataSourceId: string;
  roleName: string;
}

export type GatewayCheck = "VERIFY" | "BYPASS" | "RESOLVE";

export interface GatewayCheckRequest extends AgentProtectionQuery {
  mcpServiceId: string;
  check: GatewayCheck;
}

export interface ScenarioRequest extends AgentProtectionQuery {
  scenario: Partial<ProtectionScenario>;
}

export interface ProtectionRecommendationQuery {
  environmentId: string;
  scope: AgentScope;
}

const mockOnly = (): never => {
  throw new Error(
    "AgentProtectionService is mock-only - add a src/configs/mock.ts entry and a src/api/mock-data registry entry.",
  );
};

/**
 * Regular static METHODS, not arrow fields: the mock layer resolves registry
 * entries by `method.name`.
 */
export class AgentProtectionService {
  static getAgentProtectionUsingGet(
    _params: AgentProtectionQuery,
  ): Promise<AgentProtection> {
    return Promise.resolve(mockOnly());
  }

  static listProtectionCandidatesUsingGet(
    _params: AgentProtectionQuery,
  ): Promise<ProtectionCandidates> {
    return Promise.resolve(mockOnly());
  }

  static getDataSourceCatalogUsingGet(
    _params: AgentProtectionQuery & { dataSourceId: string },
  ): Promise<DataSourceCatalog> {
    return Promise.resolve(mockOnly());
  }

  /** Per-item results: a partial failure resolves, it does not reject. */
  static associateAgentResourcesUsingPost(
    _params: AssociateResourcesRequest,
  ): Promise<AssociationResult[]> {
    return Promise.resolve(mockOnly());
  }

  static updateAgentResourceAssociationUsingPut(
    _params: UpdateAssociationRequest,
  ): Promise<AgentResourceLink> {
    return Promise.resolve(mockOnly());
  }

  static removeAgentResourceAssociationUsingDelete(
    _params: RemoveAssociationRequest,
  ): Promise<{ resourceId: string }> {
    return Promise.resolve(mockOnly());
  }

  static saveNativePolicyDraftUsingPost(
    _params: SaveNativePolicyRequest,
  ): Promise<NativePolicy> {
    return Promise.resolve(mockOnly());
  }

  static verifyNativeRoleMappingUsingPost(
    _params: VerifyMappingRequest,
  ): Promise<{ roleName: string; verification: MappingVerification }> {
    return Promise.resolve(mockOnly());
  }

  static deployProtectionChangesUsingPost(
    _params: DeployChangesRequest,
  ): Promise<DeploymentRun> {
    return Promise.resolve(mockOnly());
  }

  /** Re-runs only the run's failed operations. Idempotent. */
  static retryFailedProtectionOperationsUsingPost(
    _params: RetryFailedRequest,
  ): Promise<DeploymentRun> {
    return Promise.resolve(mockOnly());
  }

  static reassessAgentProtectionUsingPost(
    _params: AgentProtectionQuery,
  ): Promise<ProtectionAssessment> {
    return Promise.resolve(mockOnly());
  }

  static simulateGatewayCheckUsingPost(
    _params: GatewayCheckRequest,
  ): Promise<GatewayPath> {
    return Promise.resolve(mockOnly());
  }

  static setProtectionScenarioUsingPut(
    _params: ScenarioRequest,
  ): Promise<ProtectionScenario> {
    return Promise.resolve(mockOnly());
  }

  static resetAgentProtectionDemoUsingPost(
    _params: AgentProtectionQuery,
  ): Promise<AgentProtection> {
    return Promise.resolve(mockOnly());
  }
}

/**
 * The recommendation endpoints the seeded agent's recommendations are served
 * through. They are the existing estate methods: the registry consults the
 * protection store first for a scope of exactly the seeded agent.
 */
export type ProtectionRecommendationHandlers = {
  resolveRecommendations: (
    params: ProtectionRecommendationQuery,
  ) => AgentPolicyRecommendation[] | undefined;
  resolveCoverage: (
    params: ProtectionRecommendationQuery,
  ) => AgentPolicyCoverage | undefined;
  getPolicyReview: (params: PolicyReviewQuery) => PolicyReview | undefined;
  previewPolicies: (
    params: ApplyPoliciesRequest,
  ) => PolicyApplicationPreview | undefined;
  applyPolicies: (params: ApplyPoliciesRequest) => ApplyPoliciesResult | undefined;
};
